import { useState, useRef, useEffect, useCallback, lazy, Suspense } from "react";
import { EVOHR_LOGO } from "@/lib/logo";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/shared/ThemeToggle";
import { useDeferredTranslation } from "@/hooks/useDeferredTranslation";
import {
  EvoTeamIcon, EvoPayrollIcon, EvoCRMIcon, EvoOpsIcon, EvoCommIcon,
  EvoHotelIcon, EvoRestaurantIcon, EvoRecruitIcon,
  EvoHealthIcon, EvoEduIcon, EvoRetailIcon, EvoConstructIcon,
  EvoTechIcon, EvoManufactIcon, EvoFinanceIcon, EvoGovIcon,
  EvoBlogIcon, EvoHelpIcon, EvoAPIIcon, EvoCaseStudyIcon, EvoWebinarIcon, EvoStatusIcon,
} from "./EvoIcons";

// Lazy-load auth-dependent components — keeps Supabase SDK out of critical path
const NavAuthSection = lazy(() => import("./NavAuthSection"));
const LanguageSwitcher = lazy(() => import("@/components/shared/LanguageSwitcher").then(m => ({ default: m.LanguageSwitcher })));

// Static identifiers (icons + routes); user-visible strings come from i18n via buildItems().
const solutionsKeys = [
  { icon: EvoTeamIcon, labelKey: "landing.premium.nav.solutionsItems.evohrPlatform",  descKey: "landing.premium.nav.solutionsItems.evohrPlatformDesc",  to: "/solutions/evohr-platform" },
  { icon: EvoCRMIcon,  labelKey: "landing.premium.nav.solutionsItems.evobusiness",    descKey: "landing.premium.nav.solutionsItems.evobusinessDesc",    to: "/solutions/evobusiness" },
  { icon: EvoHotelIcon,labelKey: "landing.premium.nav.solutionsItems.evohospitality", descKey: "landing.premium.nav.solutionsItems.evohospitalityDesc", to: "/solutions/evohospitality" },
  { icon: EvoRecruitIcon, labelKey: "landing.premium.nav.solutionsItems.evoai",       descKey: "landing.premium.nav.solutionsItems.evoaiDesc",          to: "/solutions/evoai" },
] as const;

const industriesKeys = [
  { icon: EvoHotelIcon,      labelKey: "landing.premium.nav.industriesItems.hospitality",        descKey: "landing.premium.nav.industriesItems.hospitalityDesc",        to: "/industries/hospitality" },
  { icon: EvoRestaurantIcon, labelKey: "landing.premium.nav.industriesItems.restaurants",        descKey: "landing.premium.nav.industriesItems.restaurantsDesc",        to: "/industries/restaurants" },
  { icon: EvoHealthIcon,     labelKey: "landing.premium.nav.industriesItems.healthcare",         descKey: "landing.premium.nav.industriesItems.healthcareDesc",         to: "/industries/healthcare" },
  { icon: EvoEduIcon,        labelKey: "landing.premium.nav.industriesItems.training",           descKey: "landing.premium.nav.industriesItems.trainingDesc",           to: "/industries/training" },
  { icon: EvoRetailIcon,     labelKey: "landing.premium.nav.industriesItems.retail",             descKey: "landing.premium.nav.industriesItems.retailDesc",             to: "/industries/retail" },
  { icon: EvoTechIcon,       labelKey: "landing.premium.nav.industriesItems.technology",         descKey: "landing.premium.nav.industriesItems.technologyDesc",         to: "/industries/technology" },
  { icon: EvoFinanceIcon,    labelKey: "landing.premium.nav.industriesItems.financialServices",  descKey: "landing.premium.nav.industriesItems.financialServicesDesc",  to: "/industries/financial-services" },
  { icon: EvoGovIcon,        labelKey: "landing.premium.nav.industriesItems.government",         descKey: "landing.premium.nav.industriesItems.governmentDesc",         to: "/industries/government" },
] as const;

const resourcesKeys = [
  { icon: EvoBlogIcon,      labelKey: "landing.premium.nav.resourcesItems.blog",        descKey: "landing.premium.nav.resourcesItems.blogDesc",        to: "/blog" },
  { icon: EvoHelpIcon,      labelKey: "landing.premium.nav.resourcesItems.helpCenter",  descKey: "landing.premium.nav.resourcesItems.helpCenterDesc",  to: "/help-center" },
  { icon: EvoCaseStudyIcon, labelKey: "landing.premium.nav.resourcesItems.caseStudies", descKey: "landing.premium.nav.resourcesItems.caseStudiesDesc", to: "/case-studies" },
] as const;

type DropdownKey = "solutions" | "industries" | "resources" | null;

export function PremiumNav({
  onRequestDemo,
}: { onRequestDemo: () => void }) {
  const { t } = useDeferredTranslation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileAccordion, setMobileAccordion] = useState<DropdownKey>(null);
  const [activeDropdown, setActiveDropdown] = useState<DropdownKey>(null);
  const [authReady, setAuthReady] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navRef = useRef<HTMLElement>(null);

  const openDropdown = (key: DropdownKey) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setActiveDropdown(key);
  };

  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setActiveDropdown(null), 200);
  };

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Defer auth section load until after first paint to keep supabase out of critical path
  useEffect(() => {
    const id = setTimeout(() => setAuthReady(true), 50);
    return () => clearTimeout(id);
  }, []);

  const navItems = [
    { label: t("landing.premium.nav.solutions", "Solutions"), to: "/solutions", dropdownKey: "solutions" as DropdownKey },
    { label: t("landing.premium.nav.industries", "Industries"), to: "/industries", dropdownKey: "industries" as DropdownKey },
    { label: t("landing.premium.nav.resources", "Resources"), to: "/resources", dropdownKey: "resources" as DropdownKey },
    { label: t("landing.premium.nav.pricing", "Pricing"), to: "/pricing" },
  ];

  // Build i18n-resolved menu items from the static keys
  const solutionsItems = solutionsKeys.map(s => ({ icon: s.icon, label: t(s.labelKey), desc: t(s.descKey), to: s.to }));
  const industriesItems = industriesKeys.map(s => ({ icon: s.icon, label: t(s.labelKey), desc: t(s.descKey), to: s.to }));
  const resourcesItems = resourcesKeys.map(s => ({ icon: s.icon, label: t(s.labelKey), desc: t(s.descKey), to: s.to }));

  return (
    <nav ref={navRef} aria-label="Main navigation" className="fixed top-0 left-0 right-0 z-50 bg-background/90 supports-[backdrop-filter]:backdrop-blur-xl supports-[backdrop-filter]:bg-background/80 border-b border-border/10">
      <div className="mx-auto max-w-7xl px-6 lg:px-8 flex items-center justify-between h-[72px]">
        <Link to="/" className="flex items-center gap-2.5">
          <img src={EVOHR_LOGO} alt="EvoHR" className="h-9 w-auto evohr-logo-gold" width={72} height={36} fetchPriority="high" />
          <span className="hidden sm:block text-[9px] text-muted-foreground/70 font-medium border-l border-border/20 pl-2 leading-tight whitespace-pre-line">
            {t("landing.premium.nav.brandTagline", "Enterprise.\nEvolved.")}
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-7">
          {navItems.map((item) => (
            <div
              key={item.label}
              className="relative"
              onMouseEnter={() => item.dropdownKey ? openDropdown(item.dropdownKey) : setActiveDropdown(null)}
              onMouseLeave={scheduleClose}
            >
              <div className="flex items-center gap-1 text-[13px] font-medium text-muted-foreground hover:text-foreground transition-colors duration-300">
                <Link
                  to={item.to}
                  onClick={() => setActiveDropdown(null)}
                  className="hover:text-foreground transition-colors"
                >
                  {item.label}
                </Link>
                {item.dropdownKey && (
                  <button
                    aria-expanded={activeDropdown === item.dropdownKey}
                    aria-haspopup="true"
                    aria-label={t("landing.premium.nav.toggleMenu", "Toggle {{label}} menu").replace("{{label}}", item.label)}
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveDropdown(activeDropdown === item.dropdownKey ? null : item.dropdownKey);
                    }}
                    className="p-0.5 hover:text-foreground transition-colors focus-visible:outline-2 focus-visible:outline-gold focus-visible:outline-offset-2 rounded"
                  >
                    <ChevronDown className={`h-3 w-3 opacity-50 transition-transform duration-200 ${activeDropdown === item.dropdownKey ? "rotate-180" : ""}`} />
                  </button>
                )}
              </div>

              {item.dropdownKey === "solutions" && activeDropdown === "solutions" && (
                <div
                  className="absolute top-full left-0 pt-3 animate-fade-in"
                  onMouseEnter={() => openDropdown("solutions")}
                  onMouseLeave={scheduleClose}
                >
                  <div role="menu" aria-label="Solutions" className="w-[420px] rounded-xl border border-gold/10 bg-popover shadow-2xl shadow-foreground/10 p-5">
                    <div className="space-y-1">
                      {solutionsItems.map((sol) => (
                        <Link
                          key={sol.label}
                          to={sol.to}
                          onClick={() => setActiveDropdown(null)}
                          className="flex items-start gap-3 rounded-lg px-3 py-3 hover:bg-gold/5 transition-colors group"
                        >
                          <sol.icon size={22} className="mt-0.5 shrink-0" />
                          <div>
                            <div className="text-[13px] font-semibold text-foreground group-hover:text-gold transition-colors">{sol.label}</div>
                            <div className="text-[11px] text-muted-foreground leading-snug">{sol.desc}</div>
                          </div>
                        </Link>
                      ))}
                    </div>
                    <div className="mt-3 pt-3 border-t border-border/20">
                      <Link
                        to="/solutions"
                        onClick={() => setActiveDropdown(null)}
                        className="flex items-center gap-1 text-[12px] font-semibold text-gold hover:text-gold/80 transition-colors"
                      >
                        {t("landing.premium.nav.viewAllSolutions", "View All Solutions")} <ArrowRight className="h-3 w-3" />
                      </Link>
                    </div>
                  </div>
                </div>
              )}

              {item.dropdownKey === "industries" && activeDropdown === "industries" && (
                <div
                  className="absolute top-full left-0 pt-3 animate-fade-in"
                  onMouseEnter={() => openDropdown("industries")}
                  onMouseLeave={scheduleClose}
                >
                  <div role="menu" aria-label="Industries" className="w-[620px] rounded-xl border border-gold/10 bg-popover shadow-2xl shadow-foreground/10 p-5">
                    <div className="grid grid-cols-2 gap-1">
                      {industriesItems.map((ind) => (
                        <Link
                          key={ind.label}
                          to={ind.to}
                          onClick={() => setActiveDropdown(null)}
                          className="flex items-start gap-3 rounded-lg px-3 py-3 hover:bg-gold/5 transition-colors group"
                        >
                          <ind.icon size={22} className="mt-0.5 shrink-0" />
                          <div>
                            <div className="text-[13px] font-semibold text-foreground group-hover:text-gold transition-colors">{ind.label}</div>
                            <div className="text-[11px] text-muted-foreground leading-snug">{ind.desc}</div>
                          </div>
                        </Link>
                      ))}
                    </div>
                    <div className="mt-3 pt-3 border-t border-border/20">
                      <Link
                        to="/industries"
                        onClick={() => setActiveDropdown(null)}
                        className="flex items-center gap-1 text-[12px] font-semibold text-gold hover:text-gold/80 transition-colors"
                      >
                        {t("landing.premium.nav.viewAllIndustries", "View All Industries")} <ArrowRight className="h-3 w-3" />
                      </Link>
                    </div>
                  </div>
                </div>
              )}

              {item.dropdownKey === "resources" && activeDropdown === "resources" && (
                <div
                  className="absolute top-full left-0 pt-3 animate-fade-in"
                  onMouseEnter={() => openDropdown("resources")}
                  onMouseLeave={scheduleClose}
                >
                  <div role="menu" aria-label="Resources" className="w-[340px] rounded-xl border border-gold/10 bg-popover shadow-2xl shadow-foreground/10 p-4">
                    <div className="space-y-0.5">
                      {resourcesItems.map((res) => (
                        <Link
                          key={res.label}
                          to={res.to}
                          onClick={() => setActiveDropdown(null)}
                          className="flex items-start gap-3 rounded-lg px-3 py-2.5 hover:bg-gold/5 transition-colors group"
                        >
                          <res.icon size={20} className="mt-0.5 shrink-0" />
                          <div>
                            <div className="text-[13px] font-semibold text-foreground group-hover:text-gold transition-colors">{res.label}</div>
                            <div className="text-[11px] text-muted-foreground leading-snug">{res.desc}</div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <Suspense fallback={<div className="h-8 w-8" />}>
            <LanguageSwitcher compact variant="header" />
          </Suspense>
          <ThemeToggle />
          {authReady ? (
            <Suspense fallback={<div className="h-10 w-[220px]" />}>
              <NavAuthSection onRequestDemo={onRequestDemo} t={t} />
            </Suspense>
          ) : <div className="h-10 w-[220px]" />}
        </div>

        <div className="flex lg:hidden items-center gap-2">
          <Suspense fallback={<div className="h-8 w-8" />}>
            <LanguageSwitcher compact variant="header" />
          </Suspense>
          <ThemeToggle />
          <button
            aria-expanded={mobileOpen}
            aria-label={mobileOpen ? t("landing.premium.nav.closeMenu", "Close menu") : t("landing.premium.nav.openMenu", "Open menu")}
            className="text-foreground p-1 focus-visible:outline-2 focus-visible:outline-gold focus-visible:outline-offset-2 rounded"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-background/95 backdrop-blur-xl border-b border-border/10 px-6 py-6 space-y-1 max-h-[80vh] overflow-y-auto">
          {navItems.map((item) => (
            <div key={item.label}>
              {item.dropdownKey ? (
                <>
                  <button
                    aria-expanded={mobileAccordion === item.dropdownKey}
                    onClick={() => setMobileAccordion(mobileAccordion === item.dropdownKey ? null : item.dropdownKey!)}
                    className="flex items-center justify-between w-full text-sm text-muted-foreground hover:text-foreground transition-colors py-2.5 focus-visible:outline-2 focus-visible:outline-gold focus-visible:outline-offset-2 rounded"
                  >
                    {item.label}
                    <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${mobileAccordion === item.dropdownKey ? "rotate-180" : ""}`} />
                  </button>
                  {mobileAccordion === item.dropdownKey && (
                    <div className="pl-2 pb-2 space-y-0.5 animate-fade-in">
                      {(item.dropdownKey === "solutions" ? solutionsItems : item.dropdownKey === "industries" ? industriesItems : resourcesItems).map((sub) => (
                        <Link
                          key={sub.label}
                          to={sub.to}
                          onClick={() => setMobileOpen(false)}
                          className="flex items-center gap-2.5 rounded-md px-3 py-2 hover:bg-gold/5 transition-colors group"
                        >
                          <sub.icon size={18} className="shrink-0" />
                          <span className="text-[13px] text-muted-foreground group-hover:text-gold transition-colors">{sub.label}</span>
                        </Link>
                      ))}
                      {(item.dropdownKey === "solutions" || item.dropdownKey === "industries") && (
                        <Link
                          to={item.dropdownKey === "solutions" ? "/solutions" : "/industries"}
                          onClick={() => setMobileOpen(false)}
                          className="flex items-center gap-1 px-3 py-2 text-[12px] font-semibold text-gold"
                        >
                          {item.dropdownKey === "solutions" ? t("landing.premium.nav.viewAllSolutions", "View All Solutions") : t("landing.premium.nav.viewAllIndustries", "View All Industries")} <ArrowRight className="h-3 w-3" />
                        </Link>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <Link
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  className="block text-sm text-muted-foreground hover:text-foreground transition-colors py-2.5"
                >
                  {item.label}
                </Link>
              )}
            </div>
          ))}
          <div className="flex flex-col gap-3 pt-4 border-t border-border/10">
            {authReady ? (
              <Suspense fallback={<div className="h-10" />}>
                <NavAuthSection onRequestDemo={onRequestDemo} t={t} mobile onCloseMobile={() => setMobileOpen(false)} />
              </Suspense>
            ) : <div className="h-10" />}
          </div>
        </div>
      )}
    </nav>
  );
}
